import Link, { LinkOptions } from "@tiptap/extension-link";
import { showURLDialog } from "../URLInputDialog";
import { markInputRule, markPasteRule, getMarkRange } from "@tiptap/core";

// Simplified phone number regex for testing
const phoneRegex =
  /(?:\+?1?[\s]?[-.\s]?[\s]?)?\(?[0-9]{3}\)?[\s]?[-.\s]?[\s]?[0-9]{3}[\s]?[-.\s]?[\s]?[0-9]{4}/g;

// Extend the LinkOptions interface to include the new emailUrl and phoneUrl options
interface CustomLinkOptions extends LinkOptions {
  emailUrl?: null | ((email: string) => string);
  phoneUrl?: null | ((phone: string) => string);
}

export default Link.extend<CustomLinkOptions>({
  addOptions() {
    return {
      ...this.parent?.(),
      emailUrl: null,
      phoneUrl: null,
    };
  },

  addPasteRules() {
    return [
      ...(this.parent?.() || []),
      markPasteRule({
        find: phoneRegex,
        type: this.type,
        getAttributes: (match) => {
          if (!this.options.phoneUrl) return false;

          const originalPhoneNumber = match[0].trim();
          const cleanedNumber = originalPhoneNumber.replace(/\D/g, "");

          let formattedNumber = originalPhoneNumber;
          if (cleanedNumber.length === 10) {
            formattedNumber = `+1${cleanedNumber}`;
          } else if (
            cleanedNumber.length === 11 &&
            cleanedNumber.startsWith("1")
          ) {
            formattedNumber = `+${cleanedNumber}`;
          }

          return {
            href: `tel:${formattedNumber}`,
            "data-original-phone": originalPhoneNumber,
            target: null,
          };
        },
      }),
    ];
  },

  addInputRules() {
    return [
      ...(this.parent?.() || []),
      markInputRule({
        // 1) Only one capturing group: the whole number.
        // 2) Inner groups are non-capturing (?: ... ).
        // 3) Guard against starting mid-digit with (?<!\d).
        // 4) End when next char is whitespace or end of text.
        find: /(?<!\d)((?:\+?1[\s.-]?)?(?:\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}))(?=\s$)/,
        type: this.type,
        getAttributes: (match) => {
          if (!this.options.phoneUrl) return null;

          const originalPhoneNumber = match[0].trim();
          const cleanedNumber = originalPhoneNumber.replace(/\D/g, "");

          let formattedNumber = originalPhoneNumber;
          if (cleanedNumber.length === 10) {
            formattedNumber = `+1${cleanedNumber}`;
          } else if (
            cleanedNumber.length === 11 &&
            cleanedNumber.startsWith("1")
          ) {
            formattedNumber = `+${cleanedNumber}`;
          }

          return {
            href: `tel:${formattedNumber}`,
            "data-original-phone": originalPhoneNumber,
            target: null,
          };
        },
      }),
    ];
  },

  addKeyboardShortcuts() {
    return {
      "Mod-k": () => {
        const previousUrl = this.editor.getAttributes("link").href;

        if (previousUrl) {
          this.editor.view.dom.dispatchEvent(new CustomEvent("edit-link"));
          return true;
        }

        const { state } = this.editor;
        const { selection } = state;
        const linkType = state.schema.marks.link;

        const range = getMarkRange(selection.$from, linkType);
        const currentText = range
          ? state.doc.textBetween(range.from, range.to, " ")
          : state.doc.textBetween(selection.from, selection.to, " ");

        showURLDialog(
          "",
          (url: string, newText?: string) => {
            if (url === "") return;

            const linkContent = {
              type: "text" as const,
              text: newText || currentText || url,
              marks: [{ type: "link", attrs: { href: url } }],
            };

            if (range) {
              return this.editor
                .chain()
                .focus()
                .insertContentAt(
                  { from: range.from, to: range.to },
                  linkContent,
                )
                .run();
            } else if (selection.from !== selection.to) {
              return this.editor
                .chain()
                .focus()
                .insertContentAt(
                  { from: selection.from, to: selection.to },
                  linkContent,
                )
                .run();
            } else {
              return this.editor
                .chain()
                .focus()
                .insertContent(linkContent)
                .run();
            }
          },
          currentText,
        );

        return true;
      },
    };
  },

  renderHTML({ HTMLAttributes }) {
    const href = HTMLAttributes.href;

    // Check if the link is an email
    if (this.options.emailUrl && href && href.startsWith("mailto:")) {
      // Modify the href to use the provided emailUrl option
      HTMLAttributes.href = this.options.emailUrl(href.replace("mailto:", ""));
    }

    // Check if the link is a phone number
    if (this.options.phoneUrl && href && href.startsWith("tel:")) {
      // Extract phone number from tel: link and format it
      const phoneNumber = href.replace("tel:", "");
      const cleanedNumber = phoneNumber.replace(/\D/g, "");

      let formattedNumber = phoneNumber;
      if (cleanedNumber.length === 10) {
        formattedNumber = `+1${cleanedNumber}`;
      } else if (cleanedNumber.length === 11 && cleanedNumber.startsWith("1")) {
        formattedNumber = `+${cleanedNumber}`;
      }

      HTMLAttributes.href = this.options.phoneUrl(formattedNumber);
    }

    return ["a", HTMLAttributes, 0];
  },
});
